import { computed, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';

import { AddDevicesDialogCancelledEvent } from '@shure/cloud/device-management/shared/analytics';
import { AppUpdateService } from '@shure/cloud/device-management/shared/services';
import { PrismDialogRef } from '@shure/prism-angular-components';
import { FooterButton, StepConfig } from '@shure/shared/angular/ui/components/step-router';
import { AnalyticsService } from '@shure/shared/angular/utils/analytics';

import { AddDevicesDialogService, MacSerialNumberDevice } from '../../../../services/add-devices-dialog.service';
import { QrCodeClaimingService } from '../../../../services/qr-code-claiming.service';
import { AddDevicesDialogComponent } from '../add-devices-dialog.component';

import { Step2DiscoverDevicesComponent } from './step2-discover-devices.component';

export enum QrCodeClaimStep {
	Scan = 'scan',
	Confirm = 'confirm'
}

@Injectable()
export class Step2DiscoverDevicesConfig implements StepConfig {
	public readonly activeQrCodeClaimStep = signal(QrCodeClaimStep.Scan);
	public readonly selectionCount = signal(0);
	private readonly scannedQrCodes = toSignal(this.qrCodeClaimingService.getScannedQrCodes$(), { initialValue: [] });
	private readonly claimMethod = toSignal(this.addDevicesDialogService.deviceDiscoveryMethod$);
	public readonly extractedDevices = signal<MacSerialNumberDevice[]>([]);

	public readonly isQrCodeNextButtonEnabled = computed(() => {
		const hasScannedQrCodes = this.scannedQrCodes().length > 0;
		const isAppReadOnly = this.appUpdateService.isReadOnly();
		return hasScannedQrCodes && !isAppReadOnly;
	});

	public readonly isAddDevicesButtonEnabled = computed(() => {
		const hasSelectedDevices = this.selectionCount() > 0;
		const isAppReadOnly = this.appUpdateService.isReadOnly();
		return hasSelectedDevices && !isAppReadOnly;
	});

	public readonly id = 'step2-discover-devices';
	public readonly label = this.transloco.translate('cloud.add-devices-dialog.discover-devices');
	public readonly component = signal(Step2DiscoverDevicesComponent);
	public readonly footerButtons = computed(() => {
		const right: FooterButton[] = [];

		// If we're on the scan step, we need to show the "Next" button
		if (this.claimMethod() === 'ClaimByQrCode' && this.activeQrCodeClaimStep() === QrCodeClaimStep.Scan) {
			right.push({
				text: this.transloco.translate('cloud.shared.button-text.next'),
				id: 'sh-add-devices-step2-next',
				testId: 'step2-next-button',
				isEnabled: this.isQrCodeNextButtonEnabled,
				onClick: () => {
					this.extractedDevices.set(
						this.scannedQrCodes().map((code) => ({
							device: {
								id: code.mac,
								macAddress: code.mac,
								serialNumber: code.serial
							}
						}))
					);
					this.activeQrCodeClaimStep.set(QrCodeClaimStep.Confirm);
				}
			});
		} else {
			// Otherwise, show the "Add devices" button
			let text;
			if (this.selectionCount() === 0) {
				text = this.transloco.translate('cloud.add-devices-dialog.select-devices');
			} else if (this.selectionCount() === 1) {
				text = this.transloco.translate('cloud.add-devices-dialog.claim-1-device');
			} else {
				text = this.transloco.translate('cloud.add-devices-dialog.claim-n-devices', {
					n: this.selectionCount()
				});
			}

			right.push({
				text,
				id: 'sh-add-devices-step2-next',
				testId: 'step2-next-button',
				isEnabled: this.isAddDevicesButtonEnabled,
				onClick: (stepRouter) => {
					stepRouter.nextStep();
				}
			});
		}

		return {
			left: [
				{
					text: this.transloco.translate('cloud.shared.button-text.cancel'),
					id: 'sh-add-devices-step2-cancel',
					testId: 'step2-cancel-button',
					onClick: (): void => {
						this.dialogRef.close();
						this.sendAddDevicesDialogCancelledEvent();
					}
				}
			],
			right
		};
	});

	constructor(
		private readonly transloco: TranslocoService,
		private readonly addDevicesDialogService: AddDevicesDialogService,
		private readonly qrCodeClaimingService: QrCodeClaimingService,
		private readonly dialogRef: PrismDialogRef<AddDevicesDialogComponent>,
		private readonly appUpdateService: AppUpdateService,
		private readonly analyticsService: AnalyticsService
	) {}

	private sendAddDevicesDialogCancelledEvent(): void {
		this.analyticsService.trackEvent(new AddDevicesDialogCancelledEvent('step_2'));
	}
}
